import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useState, useMemo } from 'react';
import { useRouter } from 'next/router';

import BellIcon from 'icons/BellIcon';
import { useUser } from 'context/user';
import { THERA_PROD_COMPANY_ID } from 'lib/whitelistConstants';
import { AdminRoutes } from 'routes';

interface Notification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  type: 'payroll' | 'invoices' | 'expenses';
  actionText?: string;
  actionRoute?: string;
}

const DUMMY_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    title: 'Payroll Due Reminder',
    message: 'Your next payroll run is due in 2 days. Make sure to approve and fund payroll on time to avoid delays.',
    timestamp: '2 hours ago',
    isRead: false,
    type: 'payroll',
    actionText: 'Pay Invoices',
    actionRoute: AdminRoutes.USPayroll,
  },
  {
    id: '2',
    title: '4 Invoices Pending Review',
    message: 'You have 2 invoices due for payment in the next 2 days. Review and schedule payments now.',
    timestamp: '1 day ago',
    isRead: false,
    type: 'invoices',
    actionText: 'Go to Thera Payments',
    actionRoute: AdminRoutes.ARInvoices,
  },
  {
    id: '3',
    title: 'Introducing US Payroll Expenses',
    message: 'Now easily review, approve, and reimburse out-of-pocket employee expenses from Thera.',
    timestamp: '3 days ago',
    isRead: true,
    type: 'expenses',
    actionText: 'Go to Expenses',
    actionRoute: AdminRoutes.Expenses,
  },
];

const NotificationDropdown = () => {
  const { currentCompany } = useUser();
  const router = useRouter();
  const [notifications, setNotifications] = useState<Notification[]>(DUMMY_NOTIFICATIONS);

  // Check if user is part of Thera prod company
  const isTheraUser = useMemo(() => {
    if (!currentCompany) return false;
    return currentCompany.companyID === THERA_PROD_COMPANY_ID;
  }, [currentCompany]);

  const unreadCount = useMemo(() => {
    return notifications.filter((notification) => !notification.isRead).length;
  }, [notifications]);

  // Don't render if not Thera prod user
  if (!isTheraUser) {
    return null;
  }

  const markAsRead = (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId ? { ...notification, isRead: true } : notification,
      ),
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true })));
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'payroll':
        return '💰';
      case 'invoices':
        return '📄';
      case 'expenses':
        return '💳';
      default:
        return '📝';
    }
  };

  const handleNotificationAction = (notification: Notification) => {
    if (notification.actionRoute) {
      router.push(notification.actionRoute);
    }
  };

  return (
    <DropdownMenu.Root modal={false}>
      <div className="relative flex justify-center">
        <DropdownMenu.Trigger className="group relative flex h-8 w-8 items-center justify-center transition-all hover:rounded-full hover:bg-neutral-100">
          <BellIcon className="h-5 w-5 fill-neutral-600 group-hover:fill-neutral-900" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="w-80 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:ring-white/10 sm:w-96"
            style={{
              zIndex: 2147483647,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            <div className="border-b border-neutral-100 px-3 py-3 sm:px-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-neutral-900">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="whitespace-nowrap text-xs text-therablue hover:text-therablue-dark"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <div className="text-gray-500">
                    <div className="mb-2 text-2xl">🔔</div>
                    <div className="text-sm">No notifications yet</div>
                  </div>
                </div>
              ) : (
                notifications.map((notification) => (
                  <DropdownMenu.Item key={notification.id} onSelect={(e) => e.preventDefault()} className="p-0">
                    <div
                      className={`relative px-3 py-3 transition-colors hover:bg-gray-50 sm:px-4 ${
                        !notification.isRead ? 'bg-blue-50' : ''
                      }`}
                      onClick={(e) => {
                        // Prevent menu from closing when clicking on notification content
                        e.stopPropagation();
                      }}
                    >
                      {!notification.isRead && (
                        <div className="absolute right-2 top-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              markAsRead(notification.id);
                              // Don't close menu
                            }}
                            className="text-xs text-therablue hover:text-therablue-dark hover:underline"
                          >
                            Mark as read
                          </button>
                        </div>
                      )}

                      <div className="flex">
                        <div className="mr-3 mt-1 flex-shrink-0 text-lg">{getNotificationIcon(notification.type)}</div>
                        <div className="min-w-0 flex-1 pr-12 sm:pr-16">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p
                                className={`text-sm ${!notification.isRead ? 'font-medium text-neutral-900' : 'text-neutral-800'}`}
                              >
                                {notification.title}
                              </p>
                              <p className="mt-1 line-clamp-2 break-words text-xs text-neutral-600">
                                {notification.message}
                              </p>
                              <p className="mt-1 text-xs text-neutral-500">{notification.timestamp}</p>
                              {notification.actionText && notification.actionRoute && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleNotificationAction(notification);
                                  }}
                                  className="mt-2 break-words text-xs text-therablue hover:text-therablue-dark hover:underline"
                                >
                                  {notification.actionText}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DropdownMenu.Item>
                ))
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </div>
    </DropdownMenu.Root>
  );
};

export default NotificationDropdown;
